/**
 * THE MIXING SYSTEM IN THE OFFER - what the rental company is asked, and which stage boxes fit (SCRUM-866, parts 2 and 3).
 *
 * Owner, 27.10.2026: «В райдере запрашивается микшерная система (или микшерные системы), прокатчик предлагает подходящий
 * стейджбокс или подтверждает конкретный если запрошен. Это должно быть у нас в каталоге по любому, это не стулья.»
 *
 * NO NEW MECHANISM. The rider already says which desks it wants (`Rider.Mixing_system`, picks of a brand or a model); the
 * offer already has answers (have / substitute / band brings / no / venue) and a model link (`model_ids`). This file only
 * turns the picks into the questions the offer asks and names the stage boxes that fit a desk, from the table
 * `console-stage-boxes.ts` - nothing is invented here:
 *
 *  - the picks listed in ONE slot (FOH, or the separate monitor desk) are ALTERNATIVES (owner, 28.10.2026: «Если пульты
 *    перечислены как варианты то прокатчик предлагает один из вариантов. Если это несколько пультов (фох и монитор) то
 *    соответственно тоже»). Per slot there is ONE desk question (`console`): the rental offers one of the listed variants
 *    («ok», the chosen variant named in the line's `model` + `model_ids`; the only variant needs no naming) or a substitute. FOH
 *    and a separate monitor desk stay two independent questions - UNLESS the rider's gear list already links one of the
 *    slot's desks (`equipment_ids`): that gear line is the question then, and a second one would reserve the desk twice. Only
 *    a catalogue link counts; a name typed in a gear line is never guessed to be the desk;
 *  - every slot whose desks have a stage box in the table has ONE more question (`stage_box`) - UNLESS the rider already names
 *    a box that fits in its gear list: then that gear line is the question (the rental confirms it or replaces it there, as for
 *    any gear line) and asking again would reserve the box twice. The stage box follows the desk actually CHOSEN in the slot;
 *    while none is chosen, the boxes of every variant are offered;
 *  - the boxes offered are the table's, in its order; the page puts the rental's own warehouse first. How many boxes is not
 *    asked or computed (owner, 28.10.2026: the rider does not ask for stage boxes; the rental substitutes what it has): one.
 *
 * Never a block: a desk the table knows no box for (an analogue desk has a multicore) asks no second question, and a box the
 * rental chooses that the table does not list for the desk is theirs to choose - this file proposes, it does not forbid.
 */
import { CONSOLE_CATALOG_IDS, CONSOLE_ENGINE_CATALOG_IDS, STAGE_BOX_CATALOG_IDS } from "./catalog-key-ids";
import { CONSOLE_STAGE_BOXES } from "./console-stage-boxes";
import { eachLineOwner } from "./rider-walk";
import { CONSOLE_CATALOG_ALIASES } from "./console-catalog-links";
import { CONSOLE_BRAND_LABELS, CONSOLE_MODELS, consolePickKey, getConsoleModel } from "./system";
/** Whether the rider still asks this question of the offer. A stored answer to one it does not ask must take no part in anything. */
export function systemAsks(system, part) {
    return part === "console" ? system.askConsole : system.askStageBox;
}
/**
 * The desk the rental actually offers in a slot, as a catalogue number: the model its answer names (`model_ids[0]`, on «ok» for a
 * chosen variant or on «substitute»), else - for «ok» on a slot that lists one desk - that desk. Anything else names none.
 */
export function chosenConsoleCatalogId(system, line) {
    if (!line || (line.answer !== "ok" && line.answer !== "substitute"))
        return undefined;
    return line.model_ids?.[0] ?? (line.answer === "ok" ? system.soleConsoleCatalogId : undefined);
}
/** «Yamaha DM7» for a model; the brand's name for a brand pick. */
export function consolePickLabel(pick) {
    if (pick.kind === "brand")
        return CONSOLE_BRAND_LABELS[pick.brand];
    const model = getConsoleModel(pick.id);
    return model ? `${CONSOLE_BRAND_LABELS[model.brand]} ${model.name}` : pick.id;
}
const idOfKey = (key) => STAGE_BOX_CATALOG_IDS[key];
const STAGE_BOX_ID_SET = new Set(Object.values(STAGE_BOX_CATALOG_IDS));
const withoutHyphens = (key) => key.replace(/-/g, "");
const CATALOG_KEYS_BY_SPELLING = (() => {
    const out = new Map();
    for (const key of Object.keys(CONSOLE_CATALOG_IDS))
        out.set(withoutHyphens(key), [...(out.get(withoutHyphens(key)) ?? []), key]);
    return out;
})();
/**
 * The catalogue number of a desk of the console catalogue (`CONSOLE_MODELS`), or `undefined` when the catalogue has no row for it.
 * The key is the desk's id (a desk named by its engine - VENUE S6L (E6L-144) - finds the engine's row); where the two were spelled apart (`digico-quantum-338` / `digico-quantum338`) the row with the same
 * letters and digits is the desk - an exact match of spelling, never a look-alike, and only when exactly one row has it. Failing
 * both, the desk may be an existing row under another name (`CONSOLE_CATALOG_ALIASES`, SCRUM-876: Heritage 3000 = H3000). A desk the
 * catalogue holds as several frame variants and has no row for (dLive) names no single row, so none: that is
 * `CONSOLES_WITHOUT_CATALOG_ROW`, with the reason.
 */
export function consoleCatalogIdOf(slug) {
    const direct = CONSOLE_CATALOG_IDS[slug] ?? CONSOLE_ENGINE_CATALOG_IDS[slug];
    if (direct !== undefined)
        return direct;
    const same = CATALOG_KEYS_BY_SPELLING.get(withoutHyphens(slug));
    if (same?.length === 1)
        return CONSOLE_CATALOG_IDS[same[0]];
    const alias = CONSOLE_CATALOG_ALIASES[slug];
    return alias === undefined ? undefined : (CONSOLE_CATALOG_IDS[alias] ?? CONSOLE_ENGINE_CATALOG_IDS[alias]);
}
const CONSOLE_SLUG_OF_ID = new Map(CONSOLE_MODELS.flatMap((c) => {
    const id = consoleCatalogIdOf(c.id);
    return id === undefined ? [] : [[id, c.id]];
}));
/** The slug of the console a catalogue number is (for the desks the catalogue has a row for). */
export function consoleSlugOfCatalogId(id) {
    return CONSOLE_SLUG_OF_ID.get(id);
}
/** Catalogue keys of the boxes that fit the desks the picks admit (or the one answered desk), in the table's order, once each. */
function compatibleKeys(picks, answeredConsoleSlug) {
    const slugs = answeredConsoleSlug !== undefined
        ? [answeredConsoleSlug]
        : picks.flatMap((pick) => (pick.kind === "model" ? [pick.id] : CONSOLE_MODELS.filter((c) => c.brand === pick.brand).map((c) => c.id)));
    const out = [];
    for (const slug of slugs)
        for (const key of CONSOLE_STAGE_BOXES[slug] ?? [])
            if (!out.includes(key))
                out.push(key);
    return out;
}
/**
 * The stage boxes (catalogue numbers) that fit a slot - the table's, in its order. `answeredConsoleCatalogId` is the desk the rental
 * has chosen in its answer: a slot lists alternatives (or a brand: «any DiGiCo»), and once the rental names the SD12 the boxes are
 * the SD12's. A box the catalogue has no row for is left out (it cannot be reserved).
 */
export function stageBoxCatalogIdsFor(picks, answeredConsoleCatalogId) {
    const answered = answeredConsoleCatalogId === undefined ? undefined : consoleSlugOfCatalogId(answeredConsoleCatalogId);
    return compatibleKeys(picks, answered).map(idOfKey).filter((id) => id !== undefined);
}
/** The catalogue numbers of the desks the picks admit that the catalogue has a row for - a brand pick admits every desk of the brand. */
export function consoleCatalogIdsFor(picks) {
    const out = [];
    for (const pick of picks) {
        const slugs = pick.kind === "model" ? [pick.id] : CONSOLE_MODELS.filter((c) => c.brand === pick.brand).map((c) => c.id);
        for (const slug of slugs) {
            const id = consoleCatalogIdOf(slug);
            if (id !== undefined && !out.includes(id))
                out.push(id);
        }
    }
    return out;
}
/** Every catalogue number a gear line of the rider links - the root's and every object's. */
function linkedIds(rider) {
    const out = new Set();
    for (const owner of eachLineOwner(rider))
        for (const line of owner.gear_list)
            for (const id of line.equipment_ids ?? [])
                out.add(id);
    return out;
}
/** The stage boxes the rider's gear list names: every catalogue number on a gear line that is a stage box of the catalogue. */
export function riderNamedStageBoxIds(rider) {
    const out = [];
    for (const owner of eachLineOwner(rider)) {
        for (const line of owner.gear_list)
            for (const id of line.equipment_ids ?? [])
                if (STAGE_BOX_ID_SET.has(id) && !out.includes(id))
                    out.push(id);
    }
    return out;
}
/** The slots the rider asks for, in reading order: FOH first, then the separate monitor desk. */
export function offerSystems(rider) {
    const system = rider.Mixing_system;
    if (!system)
        return [];
    const named = riderNamedStageBoxIds(rider);
    const linked = linkedIds(rider);
    const slots = [["foh", system.foh.picks]];
    if (system.monitor.mode === "separate")
        slots.push(["monitor", system.monitor.picks]);
    const out = [];
    for (const [slot, picks] of slots) {
        if (picks.length === 0)
            continue;
        const variants = picks.map((pick) => {
            const consoleCatalogId = pick.kind === "model" ? consoleCatalogIdOf(pick.id) : undefined;
            return { pick, pickKey: consolePickKey(pick), label: consolePickLabel(pick), ...(consoleCatalogId !== undefined ? { consoleCatalogId } : {}) };
        });
        const fitting = stageBoxCatalogIdsFor(picks);
        const namedForThis = named.filter((id) => fitting.includes(id));
        const sole = variants.length === 1 ? variants[0].consoleCatalogId : undefined;
        out.push({
            slot,
            variants,
            label: variants.map((v) => v.label).join(" / "),
            ...(sole !== undefined ? { soleConsoleCatalogId: sole } : {}),
            askConsole: !variants.some((v) => v.consoleCatalogId !== undefined && linked.has(v.consoleCatalogId)),
            namedStageBoxIds: namedForThis,
            askStageBox: fitting.length > 0 && namedForThis.length === 0,
        });
    }
    return out;
}
//# sourceMappingURL=offer-system.js.map